import _ from "lodash";
import React from "react";
import PresentationCell from "./PresentationCell";
import EmptyCell from "./EmptyCell";
import RowHeader from "./RowHeader";
import BreakRow from "./BreakRow";

// Build as a class-based component as its functional equivalent was too complex
class ScheduleBody extends React.Component {
  // 15 minute slots (.25 * 60)
  SLOT_DURATION = 0.25;

  constructor({ schedule, rooms, startTime, endTime, breakStart, breakEnd }) {
    super();
    this.rooms = _.sortBy(rooms, (room) => room.number);
    this.startTime = startTime;
    this.endTime = endTime;
    this.breakStart = breakStart;
    this.breakEnd = breakEnd;

    // Start and end times of the event
    this.eventStartTime = this.timeToFloat(startTime);
    this.eventEndTime = this.timeToFloat(endTime);

    // Start and end times of the break during the event
    this.breakStartTime = this.timeToFloat(breakStart);
    this.breakEndTime = this.timeToFloat(breakEnd);

    // Group presentations by room number
    this.schedule = _.groupBy(
      _.sortBy(schedule, (p) => p.start),
      (p) => parseInt(p.room)
    );

    this.computeRoomSchedules();
  }

  timeToFloat(time) {
    const [hour, minutes] = time
      .trim()
      .split(":")
      .map((n) => parseInt(n));
    return hour + minutes / 60.0;
  }

  floatToTime(time) {
    const h = parseInt(time);
    const m = (time - h) * 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  }

  computeRoomSchedules() {
    // For each room, determine the end time of the last presentation in the room
    _.forEach(this.rooms, (room, n) => {
      room.end = _.max(
        _.map(this.schedule[n], (p) => p.start + p.duration / 60.0)
      );

      if (room.end === this.breakStartTime) {
        room.end = this.breakEndTime;
      }

      // For each room and each time slot, determine whether the room is occupied or not.
      this.determineOccupiedSlots(room);
    });
  }

  determineOccupiedSlots(room) {
    room.occupied = _.chain(
      _.range(this.eventStartTime, this.eventEndTime, this.SLOT_DURATION)
    )
      .map(() => false)
      .value();

    this.schedule[room.number].forEach((p) => {
      const duration = p.duration / 60.0;
      let current = p.start;
      const end = current + duration;

      while (Math.abs(current - end) > 0.1) {
        room.occupied[current] = true;
        current += this.SLOT_DURATION;
      }
    });
  }

  presentationsAt(time) {
    return _.map(this.rooms, (room) => {
      let roomSchedule = this.schedule[room.number];
      let rowKey = `room${room.number}-t${time}`;

      // If the room currently has a presentation starting...
      if (
        roomSchedule &&
        roomSchedule.length > 0 &&
        Math.abs(roomSchedule[0].start - time) < 0.1
      ) {
        const presentationData = roomSchedule.shift();

        return (
          <PresentationCell
            key={rowKey}
            presentation={presentationData}
            room={room}
          />
        );
      } else if (!room.occupied[time]) {
        return (
          <EmptyCell
            key={rowKey}
            start={time}
            end={this.breakStartTime}
            room={room}
          />
        );
      } else {
        // Room is occupied from a presentation already added above
        // (i.e., with rowSpan > 3)
        return null;
      }
    });
  }

  buildSchedule() {
    const timeSlots = _.range(
      this.eventStartTime,
      this.eventEndTime,
      this.SLOT_DURATION
    );

    return _.map(timeSlots, (currentTime) => {
      const slotStart = this.floatToTime(currentTime);
      const slotEnd = this.floatToTime(currentTime + this.SLOT_DURATION);

      const rowKey = `row-${slotStart}-${slotEnd}`;

      if (currentTime === this.breakStartTime) {
        return (
          <BreakRow
            key={rowKey}
            start={slotStart}
            end={slotEnd}
            rooms={this.rooms}
          />
        );
      }

      return (
        <React.Fragment key={rowKey}>
          <tr>
            <RowHeader start={slotStart} end={slotEnd} />
            {this.presentationsAt(currentTime)}
          </tr>
          <tr></tr>
          <tr></tr>
        </React.Fragment>
      );
    });
  }

  render() {
    return <tbody>{this.buildSchedule()}</tbody>;
  }
}

export default ScheduleBody;
